import { useCallback, useState } from 'react';

import { useQuery } from '@apollo/react-hooks';
import { validate as uuidValidate } from 'uuid';

import brandConfigs from 'config/brand-resolution';
import {
  lookupCustomerByEmail,
  lookupCustomerByCustomerId,
  lookupGiftCardByCardNumber,
  lookupOrderById,
  searchCardId,
  searchPartnerLinkId,
  searchStores,
} from 'remote/queries';
import { EMAIL_REGEX } from 'utils';

import { SearchResult } from './types';

export { searchResultDisplayMap } from './search-result-display-map';
export type { SearchResult };

/**
 * Link IDs have no recognisable shape, so this matches any opaque token: no
 * whitespace, and long enough to exclude store numbers. Tighten once CTC
 * confirm their ID format.
 */
export const LINK_ID_SEARCH_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{7,63}$/;

/** A UUID reaches three customer-resolving queries, so a guest can arrive twice. */
export const dedupeHits = (hits: SearchResult[]): SearchResult[] => {
  const seen = new Set<string>();

  return hits.filter((hit) => {
    const key = `${hit.__typename}:${hit.id}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);

    return true;
  });
};

export const useUniversalSearch = (
  searchTerm: string,
): {
  loading: boolean;
  hits: SearchResult[];
} => {
  const { data: giftCardByCardNumberData, loading: giftCardByCardNumberLoading } = useQuery(
    lookupGiftCardByCardNumber,
    {
      variables: { cardNumber: searchTerm.replace(/-/g, '') },
      skip: searchTerm.replace(/-/g, '').length !== 16 || EMAIL_REGEX.test(searchTerm),
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    },
  );
  const { data: searchStoresData, loading: searchStoresLoading } = useQuery(searchStores, {
    variables: { searchTerm },
    skip: !searchTerm || EMAIL_REGEX.test(searchTerm),
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });
  const { data: customerByEmailData, loading: customerByEmailLoading } = useQuery(
    lookupCustomerByEmail,
    {
      variables: { email: searchTerm },
      skip: !EMAIL_REGEX.test(searchTerm),
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    },
  );
  const { data: customerByCustomerIdData, loading: customerByCustomerIdLoading } = useQuery(
    lookupCustomerByCustomerId,
    {
      variables: { customerId: searchTerm },
      skip: !uuidValidate(searchTerm) || EMAIL_REGEX.test(searchTerm),
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    },
  );
  const { data: orderByIdData, loading: orderByIdLoading } = useQuery(lookupOrderById, {
    variables: { rbiOrderId: searchTerm },
    skip: !uuidValidate(searchTerm) || EMAIL_REGEX.test(searchTerm),
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });
  const { data: customerByCardId, loading: customerByCardIdLoading } = useQuery(searchCardId, {
    variables: { cardId: searchTerm.replace(/-/g, '') },
    // this is a TH specific feature
    // cardId is always 12 characters long and barcodes are never less than 85 chars
    skip:
      !brandConfigs.thLoyaltyEnabled ||
      (searchTerm.length < 85 && searchTerm.replace(/-/g, '').length !== 12) ||
      EMAIL_REGEX.test(searchTerm),
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  const { data: customerByPartnerLinkId, loading: customerByPartnerLinkIdLoading } = useQuery(
    searchPartnerLinkId,
    {
      variables: { linkId: searchTerm },
      // TH-only: no other brand has a partner ledger to search.
      skip:
        !brandConfigs.thLoyaltyEnabled ||
        !LINK_ID_SEARCH_PATTERN.test(searchTerm) ||
        EMAIL_REGEX.test(searchTerm),
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    },
  );

  const loading =
    customerByEmailLoading ||
    customerByCustomerIdLoading ||
    orderByIdLoading ||
    searchStoresLoading ||
    giftCardByCardNumberLoading ||
    customerByCardIdLoading ||
    customerByPartnerLinkIdLoading;

  const hits = dedupeHits(
    [
      customerByEmailData?.Customer,
      customerByCustomerIdData?.Customer,
      orderByIdData?.Order,
      orderByIdData?.Order?.customer,
      ...(searchStoresData?.stores ?? []),
      giftCardByCardNumberData?.giftCardByCardNumber,
      customerByCardId?.customerByRewardsCardId,
      customerByPartnerLinkId?.customerByPartnerLinkId,
    ].filter(Boolean),
  );

  return { loading, hits };
};

export const useUniversalSearchFocus = (): {
  focused: boolean;
  setFocused: Function;
} => {
  const [focused, setFocused] = useState(false);
  const handleFocused = useCallback((isFocused) => {
    setFocused(isFocused);
  }, []);

  return { focused, setFocused: handleFocused };
};
